import { createSlice } from '@reduxjs/toolkit';
// import { extendedApiSlice } from '../services/movieServerApi'
export interface AddMediaState {
  selectedMediaType: string | null;
  suggestedMovieTitle: string;
  currentMovieStep: number | null;
  selectedAddType: string | null;
  selectedDataSource: string | null;
  selectedItunesResult: any;
  selectedImdbResult: any;
  selectedItunesCountry: string | null;
  generalMovieData: any;
  secondaryMovieData: any;
  castCrewMovieData: any;
  imagesMovieData: any;
  languagesMovieData: any;
  chaptersMovieData: any;
  finalMovieData: any;
  // One language per sound track, in file order, or null for a
  // track nobody named. Answered on the languages step and carried
  // to the last one, which queues the muxing that needs it - the
  // only part of finishing a file that needs a person, asked early
  // so the last button does not have to wait for anybody.
  audioTrackLanguages: (string | null)[] | null;
  // Positions of the sound tracks to drop from the file, in the
  // same order. Removed while the file is written; the original
  // is not kept, which is why the step says so plainly.
  removeAudioTracks: number[];
  mainAudioTrack: number | null;
  studios: any[];
  ratings: any[];
  qualities: any[];
  formats: any[];
  sources: any[];
  genres: any[];
  countries: any[];
  tags: any[];
  collections: any[];
  languages: any[];
  accessLevels: any[];
  newMovieId: string;
  selectedMovieResult: any;
  newMovieBackground: string;
  newMovieBackgroundVerticalPosition: number;
  newMovieUlid: string;
  newMovieFilePath: string;
  // Set only when the wizard was resumed from the queue screen, so the
  // finished job can be cleared once its movie actually exists.
  queuedJobId: string | null;
}

const initialState: AddMediaState = {
  selectedMediaType: null,
  suggestedMovieTitle: '',
  currentMovieStep: 1,
  selectedAddType: null,
  selectedDataSource: null,
  selectedItunesResult: null,
  selectedImdbResult: null,
  selectedItunesCountry: null,
  generalMovieData: null,
  secondaryMovieData: null,
  castCrewMovieData: null,
  imagesMovieData: null,
  languagesMovieData: null,
  chaptersMovieData: { hasNamedChapters: false },
  finalMovieData: { editingCompleted: false, comments: '' },
  audioTrackLanguages: null,
  removeAudioTracks: [],
  mainAudioTrack: null,
  studios: [],
  ratings: [],
  qualities: [],
  formats: [],
  sources: [],
  genres: [],
  countries: [],
  tags: [],
  collections: [],
  languages: [],
  accessLevels: [],
  newMovieId: '',
  selectedMovieResult: null,
  newMovieBackground: '',
  newMovieBackgroundVerticalPosition: 0,
  newMovieUlid: '',
  newMovieFilePath: '',
  queuedJobId: null,
};

export const addMediaSlice = createSlice({
  name: 'addMedia',
  initialState,
  reducers: {
    // Carried from New Files into the movie wizard's search, so a
    // title just typed while filing the download does not have to be
    // typed again. Read by MovieStep3 as its starting value and cleared
    // there, since it is a hand-off rather than a setting.
    setSuggestedMovieTitle: (state, { payload }) => {
      state.suggestedMovieTitle = payload;
    },
    setSelectedMediaType: (state, { payload }) => {
      state.selectedMediaType = payload;
    },
    setSelectedMovieResult: (state, { payload }) => {
      state.selectedMovieResult = payload;
    },
    setSelectedItunesAndImdbResults: (state, { payload }) => {
      state.selectedItunesResult = payload.selectedItunesResult;
      state.selectedImdbResult = payload.selectedImdbResult;
      state.selectedItunesCountry = payload.selectedItunesCountry;
    },
    setCurrentMovieStep: (state, { payload }) => {
      state.currentMovieStep = payload;
    },
    setSelectedAddType: (state, { payload }) => {
      state.selectedAddType = payload;
    },
    setSelectedDataSource: (state, { payload }) => {
      state.selectedDataSource = payload;
    },
    setMovieData: (state, { payload }) => {
      state.generalMovieData = payload.generalMovieData;
      state.secondaryMovieData = payload.secondaryMovieData;
      state.castCrewMovieData = payload.castCrewMovieData;
      state.languagesMovieData = payload.languagesMovieData;
      state.studios = payload.studios;
      state.ratings = payload.ratings;
      state.qualities = payload.qualities;
      state.formats = payload.formats;
      state.sources = payload.sources;

      state.genres = payload.genres;
      state.countries = payload.countries;
      state.tags = payload.tags;
      state.collections = payload.collections;

      state.languages = payload.languages;
      state.accessLevels = payload.accessLevels;
    },
    setGeneralMovieData: (state, { payload }) => {
      state.generalMovieData = payload;
    },
    setSecondaryMovieData: (state, { payload }) => {
      state.secondaryMovieData = payload;
    },
    setCastCrewMovieData: (state, { payload }) => {
      state.castCrewMovieData = payload;
    },
    setImagesMovieData: (state, { payload }) => {
      state.imagesMovieData = payload;
    },
    setLanguagesMovieData: (state, { payload }) => {
      state.languagesMovieData = payload;
    },
    setChaptersMovieData: (state, { payload }) => {
      state.chaptersMovieData = payload;
    },
    setFinalMovieData: (state, { payload }) => {
      state.finalMovieData = payload;
    },
    setAudioTrackLanguages: (state, { payload }) => {
      state.audioTrackLanguages = payload;
    },
    setRemoveAudioTracks: (state, { payload }) => {
      state.removeAudioTracks = payload;
    },
    setMainAudioTrack: (state, { payload }) => {
      state.mainAudioTrack = payload;
    },
    setNewMovieId: (state, { payload }) => {
      state.newMovieId = payload;
    },
    setNewMovieBackground: (state, { payload }) => {
      state.newMovieBackground = payload.movieBackground;
    },
    setNewMovieBackgroundVerticalPosition: (state, { payload }) => {
      state.newMovieBackgroundVerticalPosition = payload;
    },
    setNewMovieUlid: (state, { payload }) => {
      state.newMovieUlid = payload;
    },
    setNewMovieFilePath: (state, { payload }) => {
      state.newMovieFilePath = payload;
    },
    setQueuedJobId: (state, { payload }) => {
      state.queuedJobId = payload;
    },
    resetMovieState: () => initialState,
  },
});

export const {
  setSelectedMediaType,
  setSuggestedMovieTitle,
  setSelectedItunesAndImdbResults,
  setCurrentMovieStep,
  setSelectedAddType,
  setSelectedDataSource,
  setMovieData,
  setGeneralMovieData,
  setSecondaryMovieData,
  setCastCrewMovieData,
  setImagesMovieData,
  setLanguagesMovieData,
  setChaptersMovieData,
  setFinalMovieData,
  setAudioTrackLanguages,
  setRemoveAudioTracks,
  setMainAudioTrack,
  setNewMovieId,
  setSelectedMovieResult,
  setNewMovieBackground,
  setNewMovieBackgroundVerticalPosition,
  setNewMovieUlid,
  resetMovieState,
  setNewMovieFilePath,
  setQueuedJobId,
} = addMediaSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

export default addMediaSlice.reducer;
